import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import dcqService from '../../services/dcqService';
import { ChevronDown, ChevronUp, Search, Filter, Download, Plus, X, ChevronLeft, FileText } from 'lucide-react';
import EmptyState from '../ui/EmptyState';
import PageLoader from '../ui/PageLoader';
import CreateDCQ from './CreateDCQ/CreateDCQ';
import { AuthContext } from '../../contexts/AuthContext';
import hasUserRole from '../../utils/UserUtil';

const DCQs = () => {
  const { studyID, siteID } = useParams();
  const { user } = useContext(AuthContext);
  
  // Debug log when component loads
  console.log('DCQs component loaded, user:', user);
  const [dcqs, setDcqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    type: [],
    sitename: [],
  });
  const location = useLocation();
  const navigate = useNavigate();

  const showBackButton = false;
  const showExportButton = false;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      let response;
      const pathSegments = location.pathname.split('/').filter(segment => segment);

      if (pathSegments[0] === 'studies' && pathSegments.length === 2) {
        response = await dcqService.getDCQsForStudy(pathSegments[1]);
      } else if (pathSegments[0] === 'dcq' && pathSegments.length === 1) {
        response = await dcqService.getAllDCQs();
      } else if (pathSegments[0] === 'studies' && pathSegments[2] === 'sites' && pathSegments.length === 4) {
        response = await dcqService.getDCQsforSite(pathSegments[1], pathSegments[3]);
      } else {
        throw new Error('Invalid URL path');
      }

      if (response) {
        setDcqs(response);
      }
    } catch (error) {
      console.error('Error fetching data: ', error);
      setError('An error occurred while fetching the data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchData();
  }, [fetchData, location]);

  const formatDate = (dateString) => {
    if (!dateString) return ' - ';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedDcqs = useMemo(() => {
    let sortableDcqs = [...dcqs];
    if (sortConfig.key) {
      sortableDcqs.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ascending' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'ascending' 
            ? aValue - bValue
            : bValue - aValue;
        }

        if (sortConfig.key === 'updated_at' || sortConfig.key === 'created_at') {
          return sortConfig.direction === 'ascending'
            ? new Date(aValue) - new Date(bValue)
            : new Date(bValue) - new Date(aValue);
        }

        if (aValue == null) return sortConfig.direction === 'ascending' ? 1 : -1;
        if (bValue == null) return sortConfig.direction === 'ascending' ? -1 : 1;
        return 0;
      });
    }
    return sortableDcqs;
  }, [dcqs, sortConfig]);

  const filteredDcqs = useMemo(() => {
    console.log('filteredDcqs useMemo triggered');
    console.log('sortedDcqs length:', sortedDcqs.length);
    console.log('Current user in filteredDcqs:', user);
    
    return sortedDcqs.filter(dcq => {
      // Basic search and filter checks
      const matchesSearch = Object.values(dcq).some(value => 
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesFilters = Object.entries(filters).every(([key, values]) => 
        values.length === 0 || values.includes(dcq[key])
      );
      
      // Site permission check - non-admin users should only see DCQs for their assigned sites
      let matchesSitePermissions = true;
      if (user && !hasUserRole(user, ['Program Admin', 'Project Admin']) && !user?.is_superuser) {
        console.log('Non-admin user detected, checking site permissions');
        console.log('User object keys:', Object.keys(user));
        console.log('DCQ being checked:', dcq);
        
        // TODO: Need to implement proper site assignment lookup
        // For now, show message that filtering is not working
        console.warn('Site filtering not implemented - user.assigned_sites field missing');
        // matchesSitePermissions = false; // Uncomment this to block all DCQs until backend provides site data
      }
      
      return matchesSearch && matchesFilters && matchesSitePermissions;
    });
  }, [sortedDcqs, searchTerm, filters, user]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDcqs.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          { key: 'id', label: 'DCQ#' },
          { key: 'sitename', label: 'Site' },
          { key: 'participant', label: 'Participant' },
          { key: 'type', label: 'Type' },
          { key: 'action', label: 'Action' },
          { key: 'updated_at', label: 'Last Updated' },
          { key: 'created_at', label: 'Created on' },
          { key: 'status', label: 'Status' }
        ].map(({ key, label }) => (
          <th
            key={key}
            className="th-cell cursor-pointer whitespace-nowrap"
            onClick={() => requestSort(key)}
          >
            <div className="flex items-center gap-1">
              {label}
              {sortConfig.key === key && (
                sortConfig.direction === 'ascending' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
              )}
            </div>
          </th>
        ))}
      </tr>
    </thead>
  );

  // Status colours: green new, amber needs clarification, blue waiting, purple
  // verification, orange implementation, gray closed, red anything else.
  const getStatusBadgeClass = (status) => (
    status === 'New' ? 'badge-success' :
    status === 'Needs Clarification' ? 'badge-warning' :
    status === 'Waiting for Approval' ? 'badge-info' :
    status === 'Verification' ? 'bg-purple-50 text-purple-700 ring-purple-600/15 dark:bg-purple-500/10 dark:text-purple-300 dark:ring-purple-400/25' :
    status === 'Implementation' ? 'bg-orange-50 text-orange-700 ring-orange-600/15 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-400/25' :
    status === 'Closed' ? 'badge-gray' :
    'badge-danger'
  );

  // On /dcq/ there is no study in the URL, so link through the DCQ's own study (FECOA-2190).
  const dcqHref = (dcq) => {
    const dcqStudyId = studyID || dcq.study_external_id;
    return dcqStudyId ? `/studies/${dcqStudyId}/dcq/${dcq.id}` : null;
  };

  const renderTableBody = () => (
    <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
      {currentItems.length === 0 && (
        <tr>
          <td colSpan={8}>
            <EmptyState icon={FileText} title="No DCQs found" />
          </td>
        </tr>
      )}
      {currentItems.map((dcq) => (
        <tr key={dcq.id} className="tr-interactive">
          <td className="td-cell whitespace-nowrap">
            {dcqHref(dcq) ? <Link to={dcqHref(dcq)} className="link">{dcq.id}</Link> : dcq.id}
          </td>
          <td className="td-cell whitespace-nowrap">
            {dcq.sitename || 'N/A'}
          </td>
          <td className="td-cell whitespace-nowrap">
            {dcq.participant_display_id || dcq.participant || 'N/A'}
          </td>
          <td className="td-cell whitespace-nowrap">{dcq.type || 'N/A'}</td>
          <td className="td-cell whitespace-nowrap">{dcq.action || 'N/A'}</td>
          <td className="td-cell whitespace-nowrap">{formatDate(dcq.updated_at)}</td>
          <td className="td-cell whitespace-nowrap">{formatDate(dcq.created_at)}</td>
          <td className="td-cell whitespace-nowrap">
            <span className={`badge ${getStatusBadgeClass(dcq.status)}`}>
              {dcq.status || 'N/A'}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  );

  const toggleFilter = (filterType, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterType]: prevFilters[filterType].includes(value)
        ? prevFilters[filterType].filter(item => item !== value)
        : [...prevFilters[filterType], value]
    }));
  };

  const renderFilterOptions = () => {
    const filterOptions = {
      status: ['New', 'Needs Clarification', 'Waiting for Approval', 'Verification', 'Implementation', 'Closed'],
      type: ['Query', 'Deviation', 'Other'],
      sitename: [...new Set(dcqs.map(dcq => dcq.sitename))].filter(Boolean),
    };

    return (
      <div className="mb-4 space-y-4 rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-900/40">
        {Object.entries(filterOptions).map(([filterType, options]) => (
          <div key={filterType}>
            <h3 className="mb-2 text-sm font-semibold capitalize text-gray-900 dark:text-gray-100">{filterType}</h3>
            <div className="flex flex-wrap gap-2">
              {options.map(option => (
                <button
                  key={option}
                  onClick={() => toggleFilter(filterType, option)}
                  aria-pressed={filters[filterType].includes(option)}
                  className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                    filters[filterType].includes(option)
                      ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-primary-500'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      { showBackButton && (
      <button
        onClick={() => navigate(-1)}
        className="back-link"
      >
        <ChevronLeft className="h-4 w-4" />
        <span>Back</span>
      </button>
      )}
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
          <FileText className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">Data Clarification Queries</h2>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-400" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
          <input
            type="text"
            placeholder="Search DCQs..."
            className="input pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-pressed={showFilters}
            className={`btn ${
              showFilters ? 'border border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-400/30 dark:bg-primary-400/10 dark:text-primary-300' : 'btn-secondary'
            }`}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          { showExportButton && (
          <button className="btn btn-secondary">
            <Download size={16} />
            Export
          </button>
          )}
        </div>
      </div>

      {showFilters && renderFilterOptions()}

      {isLoading ? (
        <PageLoader className="h-64" />
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                {renderTableHeader()}
                {renderTableBody()}
              </table>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing <span className="font-medium text-gray-900 dark:text-gray-100">{filteredDcqs.length === 0 ? 0 : indexOfFirstItem + 1}</span> to <span className="font-medium text-gray-900 dark:text-gray-100">{Math.min(indexOfLastItem, filteredDcqs.length)}</span> of{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100">{filteredDcqs.length}</span> results
              </p>
            </div>
            <nav className="inline-flex items-center gap-1" aria-label="Pagination">
              {Array.from({ length: Math.ceil(filteredDcqs.length / itemsPerPage) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => paginate(index + 1)}
                  aria-current={currentPage === index + 1 ? 'page' : undefined}
                  className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg px-2 text-sm font-medium transition-colors ${
                    currentPage === index + 1
                      ? 'bg-primary-600 text-white dark:bg-primary-500' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </nav>
          </div>
        </>
      )}
      {showCreateForm && (
        <CreateDCQ onClose={() => setShowCreateForm(false)} onCreated={fetchData} />
      )}
    </div>
  );
};

export default DCQs;